import type { DeviceState } from "./supabase";

export const TZ = "Pacific/Fiji";
export const OFFLINE_AFTER_MIN = 15;
export const MOVING_KMH = 3;

export type Status = "moving" | "stopped" | "late" | "offline" | "nodata";

/** V3.12.0 (Kevin): a tracker that goes quiet is shown as "No signal" (never as moving) once it has been silent for 3× its
 *  normal reporting gap (device_state.report_s, learned per tracker), at least 2 min; Offline after 15 min as before. */
export const LATE_MIN_S = 120;
export const LATE_FACTOR = 3;
export function lateAfterS(s: Pick<DeviceState, "report_s"> | null | undefined) {
  const r = Number(s?.report_s);
  const base = Number.isFinite(r) && r > 0 ? r : 60;
  return Math.min(OFFLINE_AFTER_MIN * 60, Math.max(LATE_MIN_S, base * LATE_FACTOR));
}

export const kmh = (kn: number | null | undefined) => (kn == null ? 0 : Math.round(Number(kn) * 1.852));

export function statusOf(s: DeviceState | null, now = Date.now()): Status {
  if (!s || s.lat == null) return "nodata";
  const ageS = (now - new Date(s.seen_at).getTime()) / 1000;
  if (ageS > OFFLINE_AFTER_MIN * 60) return "offline";
  if (ageS > lateAfterS(s)) return "late";
  return kmh(s.speed_kn) >= MOVING_KMH ? "moving" : "stopped";
}

/** Speed to show: only while the tracker is reporting (a quiet tracker's last speed is not its speed now). */
export const liveSpeed = (s: DeviceState | null | undefined, st: Status) =>
  s && (st === "moving" || st === "stopped") ? `${kmh(s.speed_kn)} km/h` : "—";

export const STATUS_LABEL: Record<Status, string> = {
  moving: "Moving",
  stopped: "Stopped",
  late: "No signal",
  offline: "Offline",
  nodata: "No data",
};

export function fijiTime(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-FJ", {
    timeZone: TZ, day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).format(new Date(iso));
}

export function ago(iso: string | null | undefined, now = Date.now()) {
  if (!iso) return "—";
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}
